import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import type { User } from '@/types/api';
import UserDeleteDialog from './user-delete-dialog';
import { useDeleteUserMutation } from '@/hooks/queries/useUsers';
import { usePropertiesDropdownQuery } from '@/hooks/queries/useDropdowns';
import { useCanManageUsers } from '@/hooks/usePermissions';

import { useTranslation } from 'react-i18next';

const getInitials = (name: string) => {
  const names = name.split(' ');
  const initials = names.map(n => n[0]).join('');
  return initials.toUpperCase();
};

interface UserListProps {
  users: User[];
}

export default function UserList({ users }: UserListProps) {
  const navigate = useNavigate();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { t, i18n } = useTranslation();
  const isRTL = useMemo(() => i18n.language === 'ar', [i18n.language]);
  const canManageUsers = useCanManageUsers();

  // Use API hooks for mutations
  const deleteUserMutation = useDeleteUserMutation({
    onSuccessCallback: () => {
      setDeleteDialogOpen(false);
      setSelectedUser(null);
    },
  });

  // Use properties dropdown for display names
  const { data: properties = [] } = usePropertiesDropdownQuery();

  const getPropertyName = (property_id: number | null | undefined) => {
    if (!property_id)
      return t('users.management.allProperties', 'All Properties');
    const property = properties.find(p => p.id === property_id);
    return property?.label || 'N/A';
  };

  const handleEdit = (user: User) => {
    navigate(`/users/edit-user/${user.id}`);
  };

  const handleDelete = (user: User) => {
    setSelectedUser(user);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = () => {
    if (selectedUser) {
      deleteUserMutation.mutate(selectedUser.id);
    }
  };

  return (
    <>
      {/* Mobile/Desktop responsive table */}
      <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead
                className={`text-xs sm:text-sm min-w-[200px] ${isRTL ? 'text-right' : ''}`}
              >
                {t('users.management.columns.user', 'User')}
              </TableHead>
              <TableHead
                className={`text-xs sm:text-sm min-w-[100px] ${isRTL ? 'text-right' : ''}`}
              >
                {t('users.management.columns.role', 'Role')}
              </TableHead>
              <TableHead
                className={`text-xs sm:text-sm min-w-[150px] ${isRTL ? 'text-right' : ''}`}
              >
                {t('users.management.columns.property', 'Property')}
              </TableHead>
              <TableHead
                className={`text-center text-xs sm:text-sm ${isRTL ? 'text-center' : ''}`}
              >
                {t('users.management.columns.status', 'Status')}
              </TableHead>
              {canManageUsers && (
                <TableHead
                  className={`text-xs sm:text-sm w-16 ${isRTL ? 'text-left' : 'text-right'}`}
                >
                  {t('users.management.columns.actions', 'Actions')}
                </TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.map(user => (
              <TableRow key={user.id}>
                {/* User Info - Always visible */}
                <TableCell className="py-3">
                  <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                    <Avatar className="h-8 w-8 sm:h-10 sm:w-10 shrink-0">
                      <AvatarImage
                        src={user.avatar}
                        data-ai-hint="person face"
                      />
                      <AvatarFallback className="text-xs sm:text-sm">
                        {getInitials(user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="font-medium text-sm sm:text-base truncate">
                        {user.name}
                      </div>
                      <div className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </div>
                    </div>
                  </div>
                </TableCell>

                {/* Role - Always visible */}
                <TableCell className="py-3">
                  <Badge variant="outline" className="text-xs">
                    {user.role}
                  </Badge>
                </TableCell>

                {/* Property - Always visible */}
                <TableCell className="py-3">
                  <div
                    className="text-sm truncate max-w-[120px]"
                    title={getPropertyName(user.propertyId)}
                  >
                    {getPropertyName(user.propertyId)}
                  </div>
                </TableCell>

                {/* Status - Always visible */}
                <TableCell className="text-center py-3">
                  <Badge
                    variant={
                      user.status === 'Active' ? 'secondary' : 'destructive'
                    }
                    className="text-xs px-2 py-1"
                  >
                    {user.status === 'Active'
                      ? t('users.management.statuses.active', 'Active')
                      : t('users.management.statuses.inactive', 'Inactive')}
                  </Badge>
                </TableCell>

                {/* Actions - Visible only for super_admin */}
                <TableCell className="text-right py-3">
                  {canManageUsers && (
                    <DropdownMenu dir={isRTL ? 'ltr' : 'rtl'}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <span className="sr-only">Open menu</span>
                          <MoreHorizontal className="h-3 w-3 sm:h-4 sm:w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        alignOffset={-5}
                        side="bottom"
                        sideOffset={8}
                        className="w-40"
                      >
                        <DropdownMenuLabel className="text-xs sm:text-sm">
                          {t('actions')}
                        </DropdownMenuLabel>
                        <DropdownMenuItem
                          onClick={() => handleEdit(user)}
                          className="text-xs sm:text-sm"
                        >
                          <Pencil
                            className={`h-3 w-3 sm:h-4 sm:w-4 ${isRTL ? 'ml-2' : 'mr-2'}`}
                          />
                          {t('edit')}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-destructive text-xs sm:text-sm"
                          onClick={() => handleDelete(user)}
                        >
                          <Trash2
                            className={`h-3 w-3 sm:h-4 sm:w-4 ${isRTL ? 'ml-2' : 'mr-2'}`}
                          />
                          {t('delete')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Delete Confirmation Dialog */}
      <UserDeleteDialog
        isOpen={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        selectedUser={selectedUser}
        onConfirm={confirmDelete}
      />
    </>
  );
}
